import React, { useEffect, useMemo, useState } from "react";
import { Grid } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import { Close as CloseIcon } from "@material-ui/icons";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { Modal, Labelbox, Button } from "../index";
import { Toaster } from "../../utils/common";
import { DOCUMENT_ACTION } from "../../redux/action";
import {
  buildDefaultSignatureSubject,
  buildSignatureSendPayload,
  isSignableDocumentName,
  isValidEmail,
  logSignaturePlacementDetected,
  normalizeEmailList,
  parseDocuSignConsentError,
  redirectToAdobeConnectIfNeeded,
} from "../../utils/docusignSignature";
import SignWellEmbedFrame from "../SignWellEmbedFrame/SignWellEmbedFrame";
import "./docusign-send-modal.scss";

const EMPTY_RECIPIENT = { name: "", email: "", role: "signer" };

export default function DocuSignSendModal({
  open,
  onClose,
  document,
  clientId,
  documentCategory,
  defaultSigner = {},
  onSuccess,
}) {
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [additionalRecipients, setAdditionalRecipients] = useState([]);
  const [ccEmailsText, setCcEmailsText] = useState("");
  const [passwordProtect, setPasswordProtect] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signingUrl, setSigningUrl] = useState("");
  const [embedMode, setEmbedMode] = useState("");

  const documentName =
    document?.document_name || document?.name || "Document";

  useEffect(() => {
    if (!open) return;
    setSignerName(defaultSigner?.name || "");
    setSignerEmail(defaultSigner?.email || "");
    setAdditionalRecipients([]);
    setCcEmailsText("");
    setPasswordProtect(false);
    setAccessCode("");
    setEmailSubject(buildDefaultSignatureSubject(documentName));
    setEmailMessage("");
    setSubmitting(false);
    setSigningUrl("");
    setEmbedMode("");
  }, [open, defaultSigner?.name, defaultSigner?.email, documentName]);

  const signable = useMemo(
    () => isSignableDocumentName(documentName),
    [documentName],
  );

  const updateRecipient = (index, field, value) => {
    setAdditionalRecipients((prev) =>
      prev.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const addRecipient = () => {
    setAdditionalRecipients((prev) => [...prev, { ...EMPTY_RECIPIENT }]);
  };

  const removeRecipient = (index) => {
    setAdditionalRecipients((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleClose = () => {
    if (submitting) return;
    if (embedMode === "place") onSuccess?.();
    onClose?.();
  };

  const validate = () => {
    if (!signable) {
      Toaster.error("Only PDF, DOC, and DOCX files can be sent for signature.");
      return false;
    }
    if (!clientId) {
      Toaster.error("Client is required to send a signature request.");
      return false;
    }
    if (!document?.client_document_id) {
      Toaster.error("Document id is missing.");
      return false;
    }
    if (!String(signerName || "").trim()) {
      Toaster.error("Signer name is required.");
      return false;
    }
    if (!isValidEmail(signerEmail)) {
      Toaster.error("Enter a valid primary signer email.");
      return false;
    }

    for (const recipient of additionalRecipients) {
      const hasName = String(recipient.name || "").trim();
      const hasEmail = String(recipient.email || "").trim();
      if ((hasName || hasEmail) && !isValidEmail(hasEmail)) {
        Toaster.error("Each additional recipient needs a valid email.");
        return false;
      }
    }

    const ccEmails = normalizeEmailList(ccEmailsText);
    if (ccEmails.some((email) => !isValidEmail(email))) {
      Toaster.error("One or more CC emails are invalid.");
      return false;
    }

    if (passwordProtect) {
      const code = String(accessCode || "").trim();
      if (code.length < 4) {
        Toaster.error("Access code must be at least 4 characters.");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = buildSignatureSendPayload({
      document,
      clientId,
      documentCategory,
      signerName,
      signerEmail,
      additionalRecipients: additionalRecipients.filter((recipient) =>
        isValidEmail(recipient.email),
      ),
      ccEmails: normalizeEmailList(ccEmailsText),
      passwordProtect,
      accessCode,
      emailSubject,
      emailMessage,
    });

    setSubmitting(true);
    try {
      const response = await DOCUMENT_ACTION.sendDocumentForSignature(payload);
      if (response?.status === 1) {
        logSignaturePlacementDetected(response?.data?.signature_placement);
        const editUrl = response?.data?.embedded_edit_url;
        const embeddedUrl = response?.data?.embedded_signing_url;
        if (editUrl) {
          setEmbedMode("place");
          setSigningUrl(editUrl);
          Toaster.success(
            response?.message || "Place the signature fields, then send.",
          );
          return;
        }
        Toaster.success(response?.message || "Signature request sent.");
        onSuccess?.(response?.data || response);
        if (embeddedUrl) {
          setEmbedMode("sign");
          setSigningUrl(embeddedUrl);
          return;
        }
        onClose?.();
        return;
      }
      if (redirectToAdobeConnectIfNeeded(response)) return;
      Toaster.error(response?.message || "Failed to send signature request.");
    } catch (error) {
      if (redirectToAdobeConnectIfNeeded(error)) return;
      const consentError = parseDocuSignConsentError(error);
      Toaster.error(consentError.message || "Failed to send signature request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={null}
      open={open}
      handleClose={handleClose}
      maxWidth
      className={`docusign-send-modal${signingUrl ? " docusign-send-modal--signing" : ""}`}
    >
      <div className="docusign-send-modal__shell">
        <div className="docusign-send-modal__header">
          <h3 className="docusign-send-modal__title">
            {embedMode === "place"
              ? "Place signature fields"
              : signingUrl
                ? "Sign document"
                : "Send for Signature"}
          </h3>
          <IconButton
            aria-label="close"
            className="docusign-send-modal__close"
            onClick={handleClose}
            disabled={submitting}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <div className="docusign-send-modal__body">
          {signingUrl ? (
            <SignWellEmbedFrame
              url={signingUrl}
              containerId="signwell-send-frame"
              onCompleted={() => {
                if (embedMode === "place") {
                  Toaster.success("Signature request sent.");
                  onSuccess?.();
                } else {
                  Toaster.success("Document signed.");
                }
                onClose?.();
              }}
              onClosed={() => {
                if (embedMode === "place") onSuccess?.();
                onClose?.();
              }}
              onDeclined={() => {
                Toaster.warning("Signature was declined.");
                onClose?.();
              }}
              onError={() => {
                Toaster.error("Unable to open the SignWell signing window.");
              }}
            />
          ) : (
          <>
          <p className="docusign-send-modal__doc-name">
            Document: <strong>{documentName}</strong>
          </p>
          {!signable ? (
            <p className="docusign-send-modal__warning">
              This file type cannot be sent for signature. Use PDF, DOC, or
              DOCX.
            </p>
          ) : null}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Labelbox
                type="text"
                labelname="Primary signer name"
                value={signerName}
                changeData={setSignerName}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Labelbox
                type="text"
                labelname="Primary signer email"
                value={signerEmail}
                changeData={setSignerEmail}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <div className="docusign-send-modal__section-head">
                <h4>Additional recipients (optional)</h4>
                <Button
                  type="button"
                  variant="outline"
                  text="Add recipient"
                  icon={<AddCircleOutlineIcon />}
                  handleClick={addRecipient}
                  disable={submitting}
                />
              </div>
              {additionalRecipients.length === 0 ? (
                <p className="docusign-send-modal__hint">
                  Add extra signers or CC recipients if needed.
                </p>
              ) : null}
              {additionalRecipients.map((recipient, index) => (
                <div
                  key={`recipient-${index}`}
                  className="docusign-send-modal__recipient-row"
                >
                  <Labelbox
                    type="text"
                    labelname="Name"
                    value={recipient.name}
                    changeData={(value) =>
                      updateRecipient(index, "name", value)
                    }
                  />
                  <Labelbox
                    type="text"
                    labelname="Email"
                    value={recipient.email}
                    changeData={(value) =>
                      updateRecipient(index, "email", value)
                    }
                  />
                  <Labelbox
                    type="select"
                    labelname="Role"
                    value={recipient.role}
                    changeData={(value) =>
                      updateRecipient(index, "role", value)
                    }
                    dropdown={[
                      { id: "signer", value: "Signer" },
                      { id: "cc", value: "CC (view only)" },
                    ]}
                  />
                  <button
                    type="button"
                    className="docusign-send-modal__remove-btn"
                    onClick={() => removeRecipient(index)}
                    aria-label="Remove recipient"
                    disabled={submitting}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </button>
                </div>
              ))}
            </Grid>

            <Grid item xs={12}>
              <Labelbox
                type="text"
                labelname="Extra CC emails (optional)"
                value={ccEmailsText}
                changeData={setCcEmailsText}
                placeholder="email1@example.com, email2@example.com"
              />
            </Grid>

            <Grid item xs={12}>
              <label className="docusign-send-modal__checkbox">
                <input
                  type="checkbox"
                  checked={passwordProtect}
                  onChange={(e) => setPasswordProtect(e.target.checked)}
                  disabled={submitting}
                />
                <span>Password protect this envelope (access code)</span>
              </label>
            </Grid>

            {passwordProtect ? (
              <Grid item xs={12} sm={6}>
                <Labelbox
                  type="text"
                  labelname="Access code"
                  value={accessCode}
                  changeData={setAccessCode}
                  placeholder="Minimum 4 characters"
                />
              </Grid>
            ) : null}

            <Grid item xs={12}>
              <Labelbox
                type="text"
                labelname="Email subject"
                value={emailSubject}
                changeData={setEmailSubject}
              />
            </Grid>
            <Grid item xs={12}>
              <label className="docusign-send-modal__textarea-label">
                Email message (optional)
              </label>
              <textarea
                className="docusign-send-modal__textarea"
                rows={4}
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Add a note for the signer..."
                disabled={submitting}
              />
            </Grid>
          </Grid>
          </>
          )}
        </div>

        {signingUrl ? null : (
        <div className="docusign-send-modal__actions">
          <Button
            type="button"
            variant="outline"
            ui="transparent"
            text="Cancel"
            handleClick={handleClose}
            disable={submitting}
          />
          <Button
            type="button"
            text={submitting ? "Sending…" : "Send for signature"}
            disable={submitting || !signable}
            handleClick={handleSubmit}
          />
        </div>
        )}
      </div>
    </Modal>
  );
}
